import React, { useState, useRef } from 'react';
import toast from 'react-hot-toast';
import Modal from '../common/Modal';
import { importHomebrew, importHomebrewBatch } from '../../services/homebrewStorageService';
import { validateComposedMechanic } from '../../engine/schema';

/**
 * Modal for importing composed mechanic JSON into the Storeroom.
 * Supports paste or file upload, with validation preview before save.
 */
function ImportHomebrewModal({ userId, onClose, onImported }) {
  const [jsonText, setJsonText] = useState('');
  const [preview, setPreview] = useState(null); // { mechanic, errors } or null
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  const parseAndPreview = (text) => {
    setJsonText(text);
    setPreview(null);

    if (!text.trim()) return;

    let parsed;
    try {
      parsed = JSON.parse(text);
    } catch {
      setPreview({ mechanic: null, errors: ['Invalid JSON: could not parse input'] });
      return;
    }

    // Handle batch (array) vs single
    if (Array.isArray(parsed)) {
      const results = parsed.map((item, i) => {
        const mechanic = item.data && item.type ? item.data : item;
        const { valid, errors } = validateComposedMechanic(mechanic);
        return { index: i, name: mechanic.meta?.name || `Item ${i + 1}`, valid, errors };
      });
      setPreview({ batch: results, count: parsed.length });
      return;
    }

    // Single mechanic: unwrap export format if needed
    const mechanic = parsed.data && parsed.type ? parsed.data : parsed;
    const { valid, errors } = validateComposedMechanic(mechanic);
    setPreview({
      mechanic,
      name: mechanic.meta?.name || 'Unnamed',
      summary: mechanic.meta?.summary || '',
      containerContext: mechanic.meta?.containerContext,
      valid,
      errors,
    });
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      parseAndPreview(text);
    };
    reader.readAsText(file);
  };

  const handleImport = async () => {
    if (!preview) return;

    setSaving(true);
    try {
      // Use the already-validated JSON text from preview to avoid re-parsing
      if (preview.batch) {
        const result = await importHomebrewBatch(userId, jsonText);
        const successCount = result.success.length;
        const failCount = result.failed.length;
        if (successCount > 0) {
          toast.success(`Imported ${successCount} mechanic${successCount > 1 ? 's' : ''}`);
        }
        if (failCount > 0) {
          toast.error(`${failCount} item${failCount > 1 ? 's' : ''} failed validation`);
        }
      } else {
        const result = await importHomebrew(userId, jsonText);
        if (result.success) {
          toast.success('Mechanic imported');
        } else {
          toast.error(result.errors?.[0] || 'Import failed');
          return;
        }
      }

      onImported();
      onClose();
    } catch (err) {
      console.error('Import failed:', err);
      toast.error('Import failed');
    } finally {
      setSaving(false);
    }
  };

  const canImport = preview && !saving && (
    preview.batch
      ? preview.batch.some(r => r.valid)
      : preview.valid
  );

  return (
    <Modal title="Import JSON" onClose={onClose}>
      <div className="space-y-4">
        {/* Input area */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm text-gray-400">
              Paste composed mechanic JSON, or upload a file
            </label>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="text-sm text-purple-400 hover:text-purple-300 transition-colors"
            >
              Upload .json
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".json"
              onChange={handleFileUpload}
              className="hidden"
            />
          </div>
          <textarea
            value={jsonText}
            onChange={(e) => parseAndPreview(e.target.value)}
            placeholder='{"id": "...", "version": "1.0.0", "meta": {...}, "actions": [...]}'
            className="w-full h-48 bg-gray-900 border border-gray-700 rounded-lg p-3 text-sm text-gray-200 font-mono resize-y focus:outline-none focus:border-purple-500"
            spellCheck={false}
          />
        </div>

        {/* Validation preview */}
        {preview && !preview.batch && (
          <div className={`rounded-lg border p-4 ${preview.valid ? 'border-green-700 bg-green-900/20' : 'border-red-700 bg-red-900/20'}`}>
            <div className="flex items-center gap-2 mb-2">
              <span className={`text-lg ${preview.valid ? 'text-green-400' : 'text-red-400'}`}>
                {preview.valid ? '\u2713' : '\u2717'}
              </span>
              <span className="font-semibold text-gray-100">{preview.name}</span>
              {preview.containerContext && (
                <span className="text-xs text-gray-400 bg-gray-700 px-2 py-0.5 rounded">
                  {preview.containerContext}
                </span>
              )}
            </div>
            {preview.summary && (
              <p className="text-sm text-gray-400 mb-2">{preview.summary}</p>
            )}
            {preview.errors.length > 0 && (
              <ul className="text-sm text-red-300 space-y-1">
                {preview.errors.map((err, i) => (
                  <li key={i} className="flex items-start gap-1">
                    <span className="text-red-400 mt-0.5">-</span>
                    <span>{err}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Batch preview */}
        {preview?.batch && (
          <div className="rounded-lg border border-gray-700 bg-gray-900/50 p-4">
            <p className="text-sm text-gray-400 mb-3">
              Batch: {preview.count} item{preview.count > 1 ? 's' : ''} detected
            </p>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {preview.batch.map((item) => (
                <div
                  key={item.index}
                  className={`flex items-center gap-2 text-sm ${item.valid ? 'text-green-300' : 'text-red-300'}`}
                >
                  <span>{item.valid ? '\u2713' : '\u2717'}</span>
                  <span>{item.name}</span>
                  {!item.valid && item.errors.length > 0 && (
                    <span className="text-xs text-red-400">({item.errors[0]})</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-400 hover:text-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleImport}
            disabled={!canImport}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-500 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {saving ? 'Importing...' : 'Import'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

export default ImportHomebrewModal;
